import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { create } from 'zustand';

import { useFavorites } from '../lib/favorites.js';
import { onSearchProvidersChanged, parseQuery, searchProviders, type SearchItem, type SearchProvider } from '../lib/search.js';
import { useShortcutVersion } from '../lib/shortcuts/index.js';
import { useWorkspace } from '../state/workspace.js';
import { ContextMenu, type MenuItem } from './ui/controls.js';
import { Icon, type IconName } from './ui/icons.js';

/**
 * The command palette (Ctrl+P: commands and files) and Search window (F3:
 * everything — commands, assets, scene entities, components, settings). One
 * window, two scopes; both are fed by the providers in lib/search.ts.
 */

export type SearchMode = 'palette' | 'search';

interface SearchWindowState {
  open: boolean;
  mode: SearchMode;
  query: string;
}

export const useSearchWindow = create<SearchWindowState>(() => ({ open: false, mode: 'palette', query: '' }));

export function openSearch(mode: SearchMode, query = ''): void {
  useSearchWindow.setState({ open: true, mode, query });
}

export function toggleSearch(mode: SearchMode): void {
  const s = useSearchWindow.getState();
  if (s.open && s.mode === mode) useSearchWindow.setState({ open: false });
  else openSearch(mode);
}

const PALETTE_PROVIDERS = ['commands', 'files'];

interface Section {
  provider: SearchProvider;
  items: SearchItem[];
}

function useProviders(): SearchProvider[] {
  const version = useSyncExternalStore(onSearchProvidersChanged, () => searchProviders().length);
  return useMemo(() => searchProviders(), [version]);
}

export function CommandPalette() {
  const { open, mode, query: initial } = useSearchWindow();
  const projectName = useWorkspace((s) => s.projectName);
  const providers = useProviders();
  useShortcutVersion();
  const favorites = useFavorites((s) => s.ids);
  const toggleFavorite = useFavorites((s) => s.toggle);

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<string>('all');
  const [cursor, setCursor] = useState(0);
  const [sections, setSections] = useState<Section[]>([]);
  const [menu, setMenu] = useState<{ x: number; y: number; item: SearchItem } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const close = () => useSearchWindow.setState({ open: false });

  // Focus synchronously: people type straight after F3 / Ctrl+P.
  useLayoutEffect(() => {
    if (!open) return;
    setQuery(initial);
    setScope('all');
    setCursor(0);
    inputRef.current?.focus();
  }, [open, mode, initial]);

  const scoped = mode === 'palette' ? providers.filter((p) => PALETTE_PROVIDERS.includes(p.id)) : providers;

  // Run the query against the providers in scope (async providers may resolve later).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const { provider: prefixed, text } = parseQuery(query);
    const chosen = prefixed ? [prefixed] : scope === 'all' ? scoped.filter((p) => !p.explicit && !(p.emptyOnly && text)) : scoped.filter((p) => p.id === scope);
    const single = chosen.length === 1;
    const limit = single ? 60 : mode === 'palette' ? 40 : 8;
    void Promise.all(
      chosen
        .filter((p) => text || p.showEmpty || single)
        .map(async (provider) => {
          try {
            const items = await provider.search(text);
            return { provider, items: [...items].sort((a, b) => b.score - a.score).slice(0, limit) };
          } catch {
            return { provider, items: [] };
          }
        }),
    ).then((result) => {
      if (cancelled) return;
      const found = result.filter((s) => s.items.length > 0);
      // With a query, the provider holding the best hit comes first.
      if (text) found.sort((a, b) => (b.items[0]?.score ?? 0) - (a.items[0]?.score ?? 0));
      setSections(found);
    });
    return () => {
      cancelled = true;
    };
  }, [open, query, scope, mode, scoped.map((p) => p.id).join()]);

  const flat = sections.flatMap((s) => s.items);
  useEffect(() => setCursor(0), [query, scope]);
  useEffect(() => {
    listRef.current?.querySelector('.is-cursor')?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  async function activate(item: SearchItem | undefined) {
    if (!item) return;
    close();
    await item.run();
  }

  if (!open) return null;

  const chips = mode === 'search' ? [{ id: 'all', label: 'All' }, ...scoped.map((p) => ({ id: p.id, label: p.label, prefix: p.prefix }))] : [];
  let index = -1;

  return (
    <div className="palette-backdrop" onMouseDown={close} role="presentation">
      <div
        className={`palette search-window is-${mode}`}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-label={mode === 'search' ? 'Search' : 'Command palette'}
      >
        <div className="search-input-row">
          <Icon name="search" size={14} />
          <input
            ref={inputRef}
            autoFocus
            className="palette-input"
            value={query}
            placeholder={mode === 'search' ? 'Search commands, assets, entities, components, settings…' : 'Type a command or a file name'}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setCursor((c) => Math.min(c + 1, flat.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                void activate(flat[cursor]);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                close();
              } else if (event.key === 'Tab' && chips.length) {
                event.preventDefault();
                const i = chips.findIndex((c) => c.id === scope);
                setScope(chips[(i + (event.shiftKey ? chips.length - 1 : 1)) % chips.length]!.id);
              }
            }}
            aria-label="Search"
          />
        </div>
        {chips.length > 0 && (
          <div className="search-chips" role="tablist">
            {chips.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={scope === c.id}
                className={`search-chip${scope === c.id ? ' is-active' : ''}`}
                title={'prefix' in c && c.prefix ? `Type "${c.prefix}" to search only ${c.label}` : undefined}
                onClick={() => {
                  setScope(c.id);
                  inputRef.current?.focus();
                }}
              >
                {c.label}
                {'prefix' in c && c.prefix && <span className="search-chip-prefix">{c.prefix}</span>}
              </button>
            ))}
          </div>
        )}
        <ul className="palette-list" ref={listRef}>
          {sections.map((section) => (
            <li key={section.provider.id} className="search-section">
              {(mode === 'search' || sections.length > 1) && <div className="search-section-head">{section.provider.label}</div>}
              <ul>
                {section.items.map((item) => {
                  index++;
                  const i = index;
                  const starred = item.commandId ? favorites.includes(item.commandId) : false;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        className={`palette-row${i === cursor ? ' is-cursor' : ''}`}
                        onMouseEnter={() => setCursor(i)}
                        onClick={() => void activate(item)}
                        onContextMenu={(e) => {
                          if (!item.commandId && !item.actions?.length) return;
                          e.preventDefault();
                          setMenu({ x: e.clientX, y: e.clientY, item });
                        }}
                      >
                        <span className="search-row-icon">{item.icon && <Icon name={item.icon as IconName} size={14} />}</span>
                        <span className="search-row-label">{item.label}</span>
                        {item.detail && <span className="search-row-detail">{item.detail}</span>}
                        {item.hint && <span className="palette-hint">{item.hint}</span>}
                        {item.commandId && (
                          <span
                            role="button"
                            className={`search-star${starred ? ' is-on' : ''}`}
                            title={starred ? 'Remove from Quick Favorites' : 'Add to Quick Favorites (Q)'}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(item.commandId!);
                            }}
                          >
                            <Icon name="sparkles" size={12} />
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
          {flat.length === 0 && <li className="muted palette-empty">{query ? 'No matches.' : 'Start typing to search.'}</li>}
        </ul>
        <div className="palette-foot">
          <span>{projectName || 'No project'}</span>
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> move · <kbd>enter</kbd> run · {chips.length > 0 && <><kbd>tab</kbd> scope · </>}
            <kbd>esc</kbd> close
          </span>
        </div>
      </div>
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={((): MenuItem[] => {
            const out: MenuItem[] = [{ id: 'run', label: menu.item.commandId ? 'Run' : 'Open', icon: 'play', run: () => void activate(menu.item) }];
            for (const action of menu.item.actions ?? [])
              out.push({
                id: `a:${action.id}`,
                label: action.label,
                icon: action.icon as IconName | undefined,
                run: () => {
                  close();
                  void action.run();
                },
              });
            const id = menu.item.commandId;
            if (id) {
              const on = favorites.includes(id);
              out.push({ id: 'fav', label: on ? 'Remove from Quick Favorites' : 'Add to Quick Favorites', icon: 'sparkles', run: () => toggleFavorite(id) });
            }
            return out;
          })()}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
